import React from "react";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import {
  MdArrowForward,
  MdCheckCircle,
  MdEvent,
  MdHistory,
  MdOutlineRefresh,
  MdSchedule,
  MdWarningAmber,
} from "react-icons/md";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import "./lesson-confirmation-rescheduling.css";

const STATUS_OPTIONS = [
  { value: "needed", label: "Needs scheduling" },
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "waived", label: "Waived" },
];

const LIST_STATUS_OPTIONS = [
  { value: "active", label: "Active — needed and scheduled" },
  ...STATUS_OPTIONS,
  { value: "all", label: "All statuses" },
];

const statusQuery = (value) => {
  if (value === "active") return "needed,scheduled";
  if (value === "all") return "needed,scheduled,completed,waived";
  return value;
};

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const statusLabel = (value) =>
  STATUS_OPTIONS.find((option) => option.value === value)?.label ||
  titleCase(value);

const cancellationLabel = (value) => {
  if (value === "teacher_cancelled") return "Teacher cancellation";
  if (value === "student_cancelled_more_than_24_hours") {
    return "Student cancellation · over 24 hours";
  }
  return titleCase(value) || "Cancellation";
};

const formatDate = (value, timeZone, pattern = "MMM d, yyyy · h:mm a zzz") => {
  if (!value) return "—";
  try {
    return formatInTimeZone(new Date(value), timeZone || "UTC", pattern);
  } catch (_error) {
    return "—";
  }
};

const normalizeError = (error, fallback) => ({
  status: error?.response?.status || null,
  code: error?.response?.data?.code || "",
  message: error?.response?.data?.error || error?.message || fallback,
});

const StatusChip = ({ status }) => (
  <span className={`lesson-rescheduling__status lesson-rescheduling__status--${status}`}>
    {statusLabel(status)}
  </span>
);

const ErrorNotice = ({ error, onRetry }) => {
  if (!error) return null;
  return (
    <div className="lesson-rescheduling__error" role="alert">
      <MdWarningAmber aria-hidden="true" />
      <div>
        <strong>{error.status === 403 ? "Administrator access required" : "Unable to continue"}</strong>
        {error.code && <code>{error.code}</code>}
        <p>{error.message}</p>
      </div>
      {onRetry && <button type="button" onClick={onRetry}>Try again</button>}
    </div>
  );
};

const QueueSkeleton = () => (
  <div className="lesson-rescheduling__skeleton" aria-label="Loading rescheduling queue">
    <div>{[1, 2, 3, 4, 5, 6].map((item) => <span key={item} />)}</div>
    {[1, 2, 3, 4].map((row) => (
      <section key={row}>{[1, 2, 3, 4, 5].map((cell) => <span key={cell} />)}</section>
    ))}
  </div>
);

const Summary = ({ summary }) => {
  const active = (summary?.needed || 0) + (summary?.scheduled || 0);
  return (
    <section className="lesson-rescheduling__summary" aria-label="Rescheduling summary">
      <div><span>Active</span><strong>{active}</strong></div>
      <div><span>Needs scheduling</span><strong>{summary?.needed || 0}</strong></div>
      <div><span>Scheduled</span><strong>{summary?.scheduled || 0}</strong></div>
      <div className={(summary?.overdue || 0) > 0 ? "danger" : ""}><span>Overdue</span><strong>{summary?.overdue || 0}</strong></div>
      <div><span>Completed</span><strong>{summary?.completed || 0}</strong></div>
      <div><span>Waived</span><strong>{summary?.waived || 0}</strong></div>
    </section>
  );
};

const deadlineCountdown = (daysRemaining) => {
  if (!Number.isInteger(daysRemaining)) return "30-day deadline";
  if (daysRemaining === 0) return "Due today";
  if (daysRemaining === 1) return "1 day left";
  if (daysRemaining > 1) return `${daysRemaining} days left`;
  if (daysRemaining === -1) return "1 day overdue";
  return `${Math.abs(daysRemaining)} days overdue`;
};

const Deadline = ({ deadline, timeZone }) => {
  const countdown = deadlineCountdown(deadline?.daysRemaining);
  return (
    <div className={`lesson-rescheduling__deadline ${deadline?.overdue ? "lesson-rescheduling__deadline--overdue" : ""}`}>
      <span>Required {formatDate(deadline?.dueAt, timeZone, "MMM d, yyyy")}</span>
      <strong>{countdown}</strong>
    </div>
  );
};

const Replacement = ({ replacement, timeZone }) => {
  if (!replacement) return <span className="lesson-rescheduling__muted">Not linked</span>;
  return (
    <div className="lesson-rescheduling__replacement">
      <strong>{formatDate(replacement.scheduledStartAt, replacement.timeZone || timeZone, "MMM d · h:mm a")}</strong>
      <small>{replacement.teacher?.name || "Teacher not recorded"}</small>
    </div>
  );
};

const History = ({ item }) => {
  if (!item.history?.length) return null;
  return (
    <details className="lesson-rescheduling__history">
      <summary><MdHistory aria-hidden="true" /> {item.history.length} workflow change{item.history.length === 1 ? "" : "s"}</summary>
      <ol>
        {[...item.history].reverse().map((entry) => (
          <li key={entry.id}>
            <div><StatusChip status={entry.previousStatus} /><MdArrowForward aria-hidden="true" /><StatusChip status={entry.nextStatus} /></div>
            <p>{entry.reason}</p>
            <small>{entry.changedById} · {formatDate(entry.changedAt, item.originalLesson.timeZone)}</small>
          </li>
        ))}
      </ol>
    </details>
  );
};

const demoReplacementCandidates = (item) => {
  const originalStart = new Date(item.originalLesson.scheduledStartAt);
  return [7, 14, 21].map((daysAfterOriginal, index) => {
    const scheduledStartAt = new Date(
      originalStart.getTime() + daysAfterOriginal * 86400000
    ).toISOString();
    return {
      calendarEventId: `demo-sandbox:${item.id}:${index + 1}`,
      teacher: item.teacher,
      student: item.student,
      family: item.family,
      scheduledStartAt,
      durationMinutes: item.originalLesson.durationMinutes,
      timeZone: item.originalLesson.timeZone,
      isMakeupLesson: true,
      linkedToAnotherRequirement: false,
      sandbox: true,
    };
  });
};

const WorkflowModal = ({ item, onClose, onSaved, sandbox = false }) => {
  const [status, setStatus] = React.useState(item.status);
  const [replacementId, setReplacementId] = React.useState(
    item.replacement?.calendarEventId || ""
  );
  const [reason, setReason] = React.useState("");
  const [candidates, setCandidates] = React.useState(() =>
    sandbox ? demoReplacementCandidates(item) : []
  );
  const [loadingCandidates, setLoadingCandidates] = React.useState(!sandbox);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const loadCandidates = React.useCallback(async () => {
    if (sandbox) {
      setCandidates(demoReplacementCandidates(item));
      setLoadingCandidates(false);
      return;
    }
    setLoadingCandidates(true);
    setError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/native/lesson-confirmations/admin/rescheduling/${encodeURIComponent(item.id)}/replacement-candidates`,
        getRequestHeaders()
      );
      setCandidates(Array.isArray(response.data?.items) ? response.data.items : []);
    } catch (requestError) {
      setError({
        ...normalizeError(requestError, "Unable to load make-up lesson candidates."),
        source: "candidates",
      });
    } finally {
      setLoadingCandidates(false);
    }
  }, [item, sandbox]);

  React.useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const replacementRequired = ["scheduled", "completed"].includes(status);
  const canSave =
    reason.trim().length >= 10 &&
    (!replacementRequired || Boolean(replacementId)) &&
    !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    if (sandbox) {
      const changedAt = new Date().toISOString();
      const replacement = replacementRequired
        ? candidates.find(
            (candidate) => candidate.calendarEventId === replacementId
          ) || null
        : null;
      const updatedItem = {
        ...item,
        version: Number(item.version || 0) + 1,
        status,
        replacement,
        updatedAt: changedAt,
        updatedById: "Demo Admin",
        history: [
          ...(item.history || []),
          {
            id: `demo-history:${item.id}:${Date.now()}`,
            previousStatus: item.status,
            nextStatus: status,
            changedById: "Demo Admin · browser only",
            reason: reason.trim(),
            changedAt,
          },
        ],
      };
      setSaving(false);
      onSaved(updatedItem);
      return;
    }
    try {
      const response = await axios.patch(
        `${baseUrl}/native/lesson-confirmations/admin/rescheduling/${encodeURIComponent(item.id)}`,
        {
          status,
          replacementCalendarEventId: replacementRequired ? replacementId : "",
          reason: reason.trim(),
          expectedVersion: item.version,
        },
        getRequestHeaders()
      );
      setSaving(false);
      onSaved(response.data?.item);
    } catch (requestError) {
      setError({
        ...normalizeError(requestError, "Unable to update this requirement."),
        source: "save",
      });
      setSaving(false);
    }
  };

  return (
    <div className="lesson-rescheduling__modal-backdrop" onMouseDown={saving ? undefined : onClose}>
      <section className="lesson-rescheduling__modal" role="dialog" aria-modal="true" aria-labelledby="rescheduling-modal-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div><MdSchedule aria-hidden="true" /></div>
          <section>
            <h2 id="rescheduling-modal-title">{sandbox ? "Try rescheduling" : "Manage"} {item.student.name || item.family.name}</h2>
            <p>{sandbox ? "Browser-only demo · " : ""}{cancellationLabel(item.cancellation.type)} on {formatDate(item.originalLesson.scheduledStartAt, item.originalLesson.timeZone, "MMM d, yyyy")}</p>
          </section>
        </header>
        <div className="lesson-rescheduling__modal-body">
          <ErrorNotice
            error={error}
            onRetry={error?.source === "candidates" ? loadCandidates : undefined}
          />
          <div className="lesson-rescheduling__modal-grid">
            <label>
              Workflow status
              <select aria-label="Workflow status" value={status} onChange={(event) => setStatus(event.target.value)}>
                {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <div className="lesson-rescheduling__fixed-deadline">
              <span>Required reschedule date</span>
              <strong>{formatDate(item.deadline?.dueAt, item.originalLesson.timeZone, "MMM d, yyyy")}</strong>
              <small>{deadlineCountdown(item.deadline?.daysRemaining)} · automatically 30 days after the original lesson</small>
            </div>
          </div>

          {replacementRequired && (
            <label className="lesson-rescheduling__candidate-field">
              Linked make-up calendar event
              <select aria-label="Linked make-up calendar event" value={replacementId} onChange={(event) => setReplacementId(event.target.value)} disabled={loadingCandidates}>
                <option value="">{loadingCandidates ? "Loading make-up lessons…" : "Select a make-up lesson"}</option>
                {item.replacement?.calendarEventId && !candidates.some((candidate) => candidate.calendarEventId === item.replacement.calendarEventId) && (
                  <option value={item.replacement.calendarEventId}>Currently linked event</option>
                )}
                {candidates.map((candidate) => (
                  <option key={candidate.calendarEventId} value={candidate.calendarEventId} disabled={candidate.linkedToAnotherRequirement}>
                    {formatDate(candidate.scheduledStartAt, candidate.timeZone || item.originalLesson.timeZone, "MMM d · h:mm a")} · {candidate.teacher?.name || "Unknown teacher"}{candidate.linkedToAnotherRequirement ? " · already linked" : ""}
                  </option>
                ))}
              </select>
              {!loadingCandidates && candidates.length === 0 && <small>Create a calendar event marked as a make-up lesson, then refresh this list.</small>}
              {!sandbox && <button type="button" className="lesson-rescheduling__inline-refresh" onClick={loadCandidates} disabled={loadingCandidates}><MdOutlineRefresh aria-hidden="true" /> Refresh make-up lessons</button>}
              {sandbox && <small>These are simulated calendar events and will never be saved.</small>}
            </label>
          )}

          <label>
            {sandbox ? "Demo reason" : "Reason for this change"}
            <textarea aria-label="Reason for rescheduling change" value={reason} onChange={(event) => setReason(event.target.value)} placeholder={sandbox ? "Try the history UI with a sample explanation…" : "Explain what changed for the permanent history…"} maxLength={1000} />
            <small>{reason.trim().length}/1000 · at least 10 characters required</small>
          </label>
        </div>
        <footer>
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className={status === "waived" ? "danger" : "primary"} onClick={save} disabled={!canSave}>
            {saving ? "Saving…" : sandbox ? `Apply demo as ${statusLabel(status)}` : `Save as ${statusLabel(status)}`}
          </button>
        </footer>
      </section>
    </div>
  );
};

const LessonConfirmationRescheduling = ({ userData }) => {
  const [dataMode, setDataMode] = React.useState("real");
  const [filters, setFilters] = React.useState({
    status: "active",
    deadlineState: "all",
    cancellationType: "all",
    teacherId: "",
    school: "",
    fromDate: "",
    toDate: "",
  });
  const [cursor, setCursor] = React.useState("");
  const [cursorHistory, setCursorHistory] = React.useState([]);
  const [result, setResult] = React.useState({
    items: [],
    summary: {},
    filterOptions: { teachers: [], schools: [], statuses: [] },
    nextCursor: null,
    deadlinePolicy: null,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const isDemoPreview = dataMode === "demo";

  const loadQueue = React.useCallback(async (requestedCursor = cursor) => {
    if (!userData?.IsAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/native/lesson-confirmations/admin/rescheduling${
          isDemoPreview ? "/demo-preview" : ""
        }`,
        {
          ...getRequestHeaders(),
          params: {
            ...filters,
            status: statusQuery(filters.status),
            limit: 50,
            ...(requestedCursor ? { cursor: requestedCursor } : {}),
          },
        }
      );
      setResult({
        items: Array.isArray(response.data?.items) ? response.data.items : [],
        summary: response.data?.summary || {},
        filterOptions: response.data?.filterOptions || { teachers: [], schools: [], statuses: [] },
        nextCursor: response.data?.nextCursor || null,
        deadlinePolicy: response.data?.deadlinePolicy || null,
      });
    } catch (requestError) {
      setError(normalizeError(requestError, "Unable to load the rescheduling queue."));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, isDemoPreview, userData?.IsAdmin]);

  React.useEffect(() => {
    if (userData?.IsAdmin) loadQueue(cursor);
  }, [cursor, loadQueue, userData?.IsAdmin]);

  if (!userData?.IsAdmin) return null;

  const updateFilter = (key, value) => {
    setCursor("");
    setCursorHistory([]);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const changeDataMode = (value) => {
    setDataMode(value);
    setCursor("");
    setCursorHistory([]);
    setSelectedItem(null);
  };

  const previousPage = () => {
    setCursorHistory((history) => {
      if (!history.length) return history;
      setCursor(history[history.length - 1] || "");
      return history.slice(0, -1);
    });
  };

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(result.nextCursor);
  };

  const handleSaved = (updatedItem) => {
    setSelectedItem(null);
    if (isDemoPreview) {
      setResult((current) => {
        const items = current.items.map((item) =>
          item.id === updatedItem.id ? updatedItem : item
        );
        const count = (status) =>
          items.filter((item) => item.status === status).length;
        return {
          ...current,
          items,
          summary: {
            ...current.summary,
            total: items.length,
            needed: count("needed"),
            scheduled: count("scheduled"),
            completed: count("completed"),
            waived: count("waived"),
            overdue: items.filter(
              (item) =>
                ["needed", "scheduled"].includes(item.status) &&
                item.deadline?.overdue
            ).length,
          },
        };
      });
      return;
    }
    loadQueue(cursor);
  };

  return (
    <>
      <header className="lesson-rescheduling__header">
        <div>
          <p className="lesson-rescheduling__eyebrow">Make-up workflow</p>
          <h1>{isDemoPreview ? "Demo Make-up Sandbox" : "Make-ups & Rescheduling"}</h1>
          <p>{isDemoPreview ? "Safely try the rescheduling workflow without saving anything." : "Track submitted real lessons that require follow-up."}</p>
        </div>
        <button type="button" onClick={() => loadQueue(cursor)} disabled={loading}><MdOutlineRefresh aria-hidden="true" /> {loading ? "Refreshing…" : "Refresh"}</button>
      </header>

      <div className={`lesson-rescheduling__policy ${isDemoPreview ? "lesson-rescheduling__policy--demo" : ""}`}>
        <MdEvent aria-hidden="true" />
        <div>
          <strong>{isDemoPreview ? "Interactive demo sandbox" : "Automatic 30-day deadline"}</strong>
          <span>
            {result.deadlinePolicy?.message || "No automatic make-up deadline policy is configured."}
            {isDemoPreview && " Changes stay in this browser view and reset when you refresh or leave the Demo Sandbox."}
            {!isDemoPreview && " Demo lessons are available separately in the Demo Sandbox."}
          </span>
        </div>
      </div>

      <section className="lesson-rescheduling__filters" aria-label="Rescheduling filters">
        <label>Data<select aria-label="Rescheduling data" value={dataMode} onChange={(event) => changeDataMode(event.target.value)}><option value="real">Real workflow</option><option value="demo">Demo Sandbox</option></select></label>
        <label>Status<select aria-label="Rescheduling status filter" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>{LIST_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <label>Deadline<select aria-label="Deadline filter" value={filters.deadlineState} onChange={(event) => updateFilter("deadlineState", event.target.value)}><option value="all">All deadlines</option><option value="overdue">Overdue</option><option value="upcoming">Upcoming</option></select></label>
        <label>Cancellation<select aria-label="Cancellation filter" value={filters.cancellationType} onChange={(event) => updateFilter("cancellationType", event.target.value)}><option value="all">All qualifying cancellations</option><option value="student">Student · over 24 hours</option><option value="teacher">Teacher cancellation</option></select></label>
        <label>School<select aria-label="Rescheduling school filter" value={filters.school} onChange={(event) => updateFilter("school", event.target.value)}><option value="">All schools</option>{(result.filterOptions.schools || []).map((school) => <option key={school} value={school}>{school}</option>)}</select></label>
        <label>Teacher<select aria-label="Rescheduling teacher filter" value={filters.teacherId} onChange={(event) => updateFilter("teacherId", event.target.value)}><option value="">All teachers</option>{(result.filterOptions.teachers || []).map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.name}</option>)}</select></label>
        <label>Original lesson from<input aria-label="Original lesson from" type="date" value={filters.fromDate} onChange={(event) => updateFilter("fromDate", event.target.value)} /></label>
        <label>Original lesson through<input aria-label="Original lesson through" type="date" value={filters.toDate} onChange={(event) => updateFilter("toDate", event.target.value)} /></label>
      </section>

      <ErrorNotice error={error} onRetry={() => loadQueue(cursor)} />
      {loading ? <QueueSkeleton /> : !error && (
        <>
          <Summary summary={result.summary} />
          {result.items.length === 0 ? (
            <div className="lesson-rescheduling__empty">
              <MdCheckCircle aria-hidden="true" />
              <h2>No matching rescheduling requirements</h2>
              <p>
                {isDemoPreview
                  ? "Submit a demo cancellation that requires rescheduling to populate this preview."
                  : "There are no submitted real lessons matching these filters."}
              </p>
            </div>
          ) : (
            <div className="lesson-rescheduling__table-card">
              <div className="lesson-rescheduling__table-scroll">
                <table className="lesson-rescheduling__table">
                  <thead><tr><th>Student &amp; family</th><th>Teacher</th><th>Original lesson</th><th>Cancellation</th><th>Requirement</th><th>Replacement</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                  <tbody>
                    {result.items.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr className={item.deadline?.overdue ? "overdue" : ""}>
                          <td><strong>{item.student.name || "Unknown student"}</strong><small>{item.family.name || "Unknown family"}</small></td>
                          <td><strong>{item.teacher.name || item.teacher.id}</strong><small>{item.school || "No school"}</small></td>
                          <td><strong>{formatDate(item.originalLesson.scheduledStartAt, item.originalLesson.timeZone, "MMM d, yyyy")}</strong><small>{formatDate(item.originalLesson.scheduledStartAt, item.originalLesson.timeZone, "h:mm a")} · {item.originalLesson.durationMinutes} min</small></td>
                          <td><strong>{cancellationLabel(item.cancellation.type)}</strong><small>{item.cancellation.reason || `Recorded by ${item.cancellation.recordedBy || "unknown"}`}</small></td>
                          <td><strong>Created {formatDate(item.requirementCreatedAt, item.originalLesson.timeZone, "MMM d, yyyy")}</strong><Deadline deadline={item.deadline} timeZone={item.originalLesson.timeZone} /></td>
                          <td><Replacement replacement={item.replacement} timeZone={item.originalLesson.timeZone} /></td>
                          <td><StatusChip status={item.status} /></td>
                          <td>
                            {isDemoPreview ? (
                              <button type="button" className="lesson-rescheduling__manage lesson-rescheduling__manage--demo" onClick={() => setSelectedItem(item)} aria-label={`Try demo rescheduling for ${item.student.name || item.family.name}`}>Try demo</button>
                            ) : (
                              <button type="button" className="lesson-rescheduling__manage" onClick={() => setSelectedItem(item)} aria-label={`Manage rescheduling for ${item.student.name || item.family.name}`}>Manage</button>
                            )}
                          </td>
                        </tr>
                        {item.history?.length > 0 && <tr className="lesson-rescheduling__history-row"><td colSpan="8"><History item={item} /></td></tr>}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer className="lesson-rescheduling__pagination"><span>{result.items.length} requirement{result.items.length === 1 ? "" : "s"} on this page</span><div><button type="button" onClick={previousPage} disabled={!cursorHistory.length}>Previous</button><button type="button" onClick={nextPage} disabled={!result.nextCursor}>Next</button></div></footer>
            </div>
          )}
        </>
      )}
      {selectedItem && <WorkflowModal item={selectedItem} sandbox={isDemoPreview} onClose={() => setSelectedItem(null)} onSaved={handleSaved} />}
    </>
  );
};

export default LessonConfirmationRescheduling;
